import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Row, Table } from 'react-bootstrap';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toastMessage } from '../../helpers/utility.js';
import { voucherStatement } from '../../redux/slices/VoucherSlice.js';
import { checkEmpty, number_format } from '../../../src/helpers/utility';

const Statement = () => {
  const location = useLocation();
  const { state } = location;
  const dispatch = useDispatch();
  const { id, type } = useParams(); // 'customer' | 'supplier'

  const [startDate, setStartDate] = useState(null); // 'YYYY-MM-DD'
  const [endDate, setEndDate] = useState(null); // 'YYYY-MM-DD'

  const [data, setData] = useState({});
  const [customerDetails, setCustomerDetails] = useState(null);
  const [supplierDetails, setSupplierDetails] = useState(null);

  const [invoiceData, setInvoiceData] = useState([]);
  const [paymentData, setPaymentData] = useState([]);

  // ---------------- Helpers: backend-like date filtering (string compare) ----------------
  const toYMD = (val) => (val ? String(val).split(' ')[0] : ''); // supports "YYYY-MM-DD HH:mm:ss"

  // Same logic as backend:
  // voucher_Date >= start_date AND voucher_Date <= end_date
  const inDateRange = (rowDate, start, end) => {
    const d = toYMD(rowDate);
    const s = start ? toYMD(start) : '';
    const e = end ? toYMD(end) : '';

    if (!d) return true; // if date missing, don't hide it
    if (s && d < s) return false;
    if (e && d > e) return false;
    return true;
  };

  const sum = (arr, key) => (arr || []).reduce((acc, x) => acc + (parseFloat(x?.[key]) || 0), 0);

  const queryRange = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return {
      fromdate: search.get('fromdate'),
      todate: search.get('todate'),
    };
  }, [location.search]);

  // ---------------- Statement Period from navigation state (reset properly) ----------------
  useEffect(() => {
    const from = state?.filter?.fromdate || state?.filter?.filter?.fromdate || queryRange.fromdate;
    const to = state?.filter?.todate || state?.filter?.filter?.todate || queryRange.todate;

    setStartDate(!checkEmpty(from) ? from : null);
    setEndDate(!checkEmpty(to) ? to : null);
  }, [state, queryRange]);

  // ---------------- Fetch statement ----------------
  useEffect(() => {
    if (!id || !type) return;
    getComponentData(id, type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, type]);

  const getComponentData = async (paramId, paramType) => {
    const normalizedType = String(paramType || '').toLowerCase();
    const voucherPaymentType =
      normalizedType === 'customer' ? 'Customer' : normalizedType === 'supplier' ? 'Supplier' : paramType;
    const parsedPaymentData = Number.parseInt(paramId, 10);
    const from = state?.filter?.fromdate || state?.filter?.filter?.fromdate || queryRange.fromdate;
    const to = state?.filter?.todate || state?.filter?.filter?.todate || queryRange.todate;
    const params = {
      filter: {
        voucher_payment_Type: voucherPaymentType,
        voucher_payment_Data: Number.isNaN(parsedPaymentData) ? paramId : parsedPaymentData,
        fromdate: !checkEmpty(from) ? from : undefined,
        todate: !checkEmpty(to) ? to : undefined,
      },
    };

    try {
      const { payload } = await dispatch(voucherStatement({ inputData: params }));
      setData(payload || {});

      if (normalizedType === 'customer') {
        const customer = Array.isArray(payload?.customer) ? payload.customer[0] : payload?.customer;

        setCustomerDetails(customer || null);
        setSupplierDetails(null);

        // ✅ Use invoice_Date (NOT created_at)
        setInvoiceData(
          (payload?.customer_invoice || []).map((invoice) => ({
            invoice_Date: invoice?.invoice_Date, // <--- IMPORTANT
            invoice_No: invoice?.invoice_No,
            invoice_Amount: invoice?.invoice_Amount ?? invoice?.invoice_Summary_TotalAmount,
          })),
        );

        // ✅ Payments: prefer voucher_payment_Date (or voucher_Date) if available, else fallback
        setPaymentData(
          (payload?.voucher_payment || []).map((payment) => ({
            payment_Date: payment?.voucher_payment_Date || payment?.voucher_Date || payment?.created_at,
            voucher_payment_Amount: payment?.voucher_payment_Amount,
            voucher_No: payment?.voucher_No,
          })),
        );
      }

      if (normalizedType === 'supplier') {
        const supplier = Array.isArray(payload?.supplier) ? payload.supplier[0] : payload?.supplier;

        setSupplierDetails(supplier || null);
        setCustomerDetails(null);

        // ✅ Use invoice_Date (NOT created_at)
        setInvoiceData(
          (payload?.supplier_invoice || []).map((invoice) => ({
            invoice_Date: invoice?.invoice_Date, // <--- IMPORTANT (ensure this exists in supplier_invoice table too)
            invoice_No: invoice?.invoice_Number,
            invoice_Amount: invoice?.total_invoice_amount,
          })),
        );

        setPaymentData(
          (payload?.voucher_payment || []).map((payment) => ({
            payment_Date: payment?.voucher_payment_Date || payment?.voucher_Date || payment?.created_at,
            voucher_payment_Amount: payment?.voucher_payment_Amount,
            voucher_No: payment?.voucher_No,
          })),
        );
      }
    } catch (err) {
      toastMessage('error', 'Failed to fetch data');
    }
  };

  // ---------------- Filter by statement period (same as backend logic) ----------------
  const filteredInvoices = useMemo(() => {
    return (invoiceData || []).filter((x) => inDateRange(x?.invoice_Date, startDate, endDate));
  }, [invoiceData, startDate, endDate]);

  const filteredPayments = useMemo(() => {
    return (paymentData || []).filter((x) => inDateRange(x?.payment_Date, startDate, endDate));
  }, [paymentData, startDate, endDate]);

  // ---------------- Totals based on filtered data ----------------
  const periodTotals = useMemo(() => {
    const invoiceTotal = sum(filteredInvoices, 'invoice_Amount');
    const paidTotal = sum(filteredPayments, 'voucher_payment_Amount');
    const dueTotal = invoiceTotal - paidTotal;
    return { invoiceTotal, paidTotal, dueTotal };
  }, [filteredInvoices, filteredPayments]);

  // ---------------- Print ----------------
  const print = () => {
    const printSection = document.getElementById('print-section');
    if (!printSection) return;

    const printWindow = window.open('', '', '');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print</title>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css">
          <link rel="stylesheet" href="${window.location.origin}/assets/css/style.css"> 
          <style>
            @page { size: A4; margin: 2.5mm; }
            html, body { width: 100%; margin: 0 !important; padding: 0 !important; }
            body {
              font-size: 10px;
              line-height: 1.15;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .print-root,
            .print-root .container,
            .print-root .container-fluid {
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
            }

            .print-root .row {
              --bs-gutter-x: 0.4rem;
              --bs-gutter-y: 0.25rem;
              margin-left: 0 !important;
              margin-right: 0 !important;
            }

            .print-root [class*='col-'],
            .print-root .col {
              padding-left: 0.2rem !important;
              padding-right: 0.2rem !important;
            }

            .print-root .card {
              border: 0 !important;
              margin: 0 0 0.35rem 0 !important;
              break-inside: auto;
            }

            .print-root .card-body { padding: 0.25rem !important; }

            .print-root p,
            .print-root h1,
            .print-root h2,
            .print-root h3,
            .print-root h4,
            .print-root h5,
            .print-root h6 {
              margin-top: 0 !important;
              margin-bottom: 0.2rem !important;
            }

            .print-root address {
              margin-bottom: 0.3rem !important;
              line-height: 1.2 !important;
            }

            .print-root .h3 { font-size: 0.95rem !important; }

            .print-root .mb-4,
            .print-root .mb-3,
            .print-root .mb-2,
            .print-root .mb-1 { margin-bottom: 0.2rem !important; }

            .print-root .card-footer,
            .print-root .print-hidden { display: none !important; }

            .print-root .table {
              width: 100%;
              table-layout: auto;
              border: 1px solid #d8d8e2 !important;
              margin-bottom: 0.3rem !important;
              word-break: break-word;
              font-size: 9px;
            }

            .print-root .table > :not(:first-child) { border-top: 1px solid #d8d8e2 !important; }

            .print-root .table th,
            .print-root .table td {
              padding: 0.14rem 0.2rem !important;
              vertical-align: middle;
              line-height: 1.1;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .print-root .bg-dark {
              background-color: #383853 !important;
              color: #fff !important;
              border: 1px solid #383853 !important;
            }

            .print-root thead { display: table-header-group; }

            .print-root .total-row {
              break-inside: avoid;
              page-break-inside: avoid;
              font-weight: 700;
            }

            @media print {
              body { margin: 0 !important; }
              .print-root { padding: 0 !important; }
            }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <div class="print-root">${printSection.innerHTML}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Row className="row-sm" id="print-section">
      <Col md={12} lg={12}>
        <Card className="custom-card">
          <Card.Body>
            <div className="d-lg-flex">
              <h6 className="main-content-label mb-1">
                <span className="d-flex mb-4">
                  <Link to={`${process.env.PUBLIC_URL}/dashboard/dashboard-1`}>
                    <img
                      src={require('../../assets/img/brand/favicon.png')}
                      className="sign-favicon ht-40"
                      alt="logo"
                    />
                  </Link>
                </span>
              </h6>
              <div className="ms-auto text-end">
                <p className="mb-1">
                  <span className="font-weight-bold">STATEMENT OF ACCOUNTS</span>
                </p>
              </div>
            </div>

            <Row className="row-sm">
              <Col sm={6} md={6} lg={6} xl={6}>
                <p className="h3">Al Fadly Trading &amp; Customs Clarance Co. W.L.L</p>
                <address>
                  PO Box : 45905
                  <br />
                  CR No : 21630
                  <br />
                  Doha - Qatar
                  <br />
                  operations@alfadlytcc.com
                </address>
              </Col>

              <Col sm={6} md={6} lg={6} xl={6} className="text-end">
                {type === 'supplier' ? (
                  <>
                    <p className="h3">{supplierDetails?.supplier_Name}</p>
                    <address>
                      {supplierDetails?.supplier_address_Line_1}{' '}
                      {!checkEmpty(supplierDetails?.supplier_address_Line_1) &&
                      !checkEmpty(supplierDetails?.supplier_address_Line_2)
                        ? ','
                        : ''}{' '}
                      {supplierDetails?.supplier_address_Line_2}
                      {!checkEmpty(supplierDetails?.supplier_address_Line_1) &&
                        !checkEmpty(supplierDetails?.supplier_address_Line_2) && <br />}
                      {supplierDetails?.supplier_address_State}{' '}
                      {!checkEmpty(supplierDetails?.supplier_address_State) &&
                      !checkEmpty(supplierDetails?.supplier_address_City)
                        ? ','
                        : ''}{' '}
                      {supplierDetails?.supplier_address_City}
                      {!checkEmpty(supplierDetails?.supplier_address_State) &&
                        !checkEmpty(supplierDetails?.supplier_address_City) && <br />}
                      {supplierDetails?.supplier_address_Country}{' '}
                      {!checkEmpty(supplierDetails?.supplier_address_Country) &&
                      !checkEmpty(supplierDetails?.supplier_address_Postcode)
                        ? ','
                        : ''}{' '}
                      {supplierDetails?.supplier_address_Postcode}
                      {!checkEmpty(supplierDetails?.supplier_address_Country) &&
                        !checkEmpty(supplierDetails?.supplier_address_Postcode) && <br />}
                      {supplierDetails?.supplier_Mobile}{' '}
                      {!checkEmpty(supplierDetails?.supplier_Mobile) && !checkEmpty(supplierDetails?.supplier_Email)
                        ? ','
                        : ''}{' '}
                      {supplierDetails?.supplier_Email}
                    </address>
                  </>
                ) : (
                  <>
                    <p className="h3">{customerDetails?.customer_Name}</p>
                    <address>
                      {customerDetails?.customer_address_Line_1}{' '}
                      {!checkEmpty(customerDetails?.customer_address_Line_1) &&
                      !checkEmpty(customerDetails?.customer_address_Line_2)
                        ? ','
                        : ''}{' '}
                      {customerDetails?.customer_address_Line_2}
                      {!checkEmpty(customerDetails?.customer_address_Line_1) &&
                        !checkEmpty(customerDetails?.customer_address_Line_2) && <br />}
                      {customerDetails?.customer_address_State}{' '}
                      {!checkEmpty(customerDetails?.customer_address_State) &&
                      !checkEmpty(customerDetails?.customer_address_City)
                        ? ','
                        : ''}{' '}
                      {customerDetails?.customer_address_City}
                      {!checkEmpty(customerDetails?.customer_address_State) &&
                        !checkEmpty(customerDetails?.customer_address_City) && <br />}
                      {customerDetails?.customer_address_Country}{' '}
                      {!checkEmpty(customerDetails?.customer_address_Country) &&
                      !checkEmpty(customerDetails?.customer_address_Postcode)
                        ? ','
                        : ''}{' '}
                      {customerDetails?.customer_address_Postcode}
                      {!checkEmpty(customerDetails?.customer_address_Country) &&
                        !checkEmpty(customerDetails?.customer_address_Postcode) && <br />}
                      {customerDetails?.customer_Mobile}{' '}
                      {!checkEmpty(customerDetails?.customer_Mobile) && !checkEmpty(customerDetails?.customer_Email)
                        ? ','
                        : ''}{' '}
                      {customerDetails?.customer_Email}
                    </address>
                  </>
                )}

                <div>
                  {(startDate || endDate) && (
                    <div style={{ marginBottom: '1rem', textAlign: 'left', fontWeight: 600 }}>
                      Statement Period: {startDate || '-'} to {endDate || '-'}
                    </div>
                  )}

                  <Table className="table table-invoice table-bordered float-end">
                    <thead>
                      <tr>
                        <th colSpan={2} className="bg-dark text-white tx-left wd-20p">
                          Account Summary
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Total Invoice Amount</td>
                        <td className="tx-right">{number_format(periodTotals.invoiceTotal, 2)}</td>
                      </tr>
                      <tr>
                        <td>Total Payment Amount</td>
                        <td className="tx-right">{number_format(periodTotals.paidTotal, 2)}</td>
                      </tr>
                      <tr className="total-row">
                        <th>TOTAL DUES</th>
                        <th className="tx-right">{number_format(periodTotals.dueTotal, 2)}</th>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </Col>
            </Row>

            <Row className="row-sm">
              <Col sm={6} md={6} lg={6} xl={6}>
                <Card>
                  <Card.Body>
                    <h5 className="card-title mb-3">Invoice Details</h5>

                    {!checkEmpty(filteredInvoices) ? (
                      <Table className="table table-invoice table-bordered">
                        <thead>
                          <tr>
                            <th className="wd-5p">#</th>
                            <th className="wd-20p">Date</th>
                            <th className="wd-40p">Description</th>
                            <th className="tx-right">Amount</th>
                          </tr>
                        </thead>

                        <tbody>
                          {filteredInvoices.map((item, index) => (
                            <tr key={`${item?.invoice_No ?? 'INV'}-${index}`}>
                              <td>{index + 1}</td>
                              <td>{toYMD(item?.invoice_Date) || '-'}</td>
                              <td className="tx-12">Invoice No # {item?.invoice_No || '-'}</td>
                              <td className="tx-right">{number_format(item?.invoice_Amount, 2)}</td>
                            </tr>
                          ))}

                          <tr className="total-row">
                            <th colSpan={3}>TOTAL</th>
                            <th className="tx-right">{number_format(periodTotals.invoiceTotal, 2)}</th>
                          </tr>
                        </tbody>
                      </Table>
                    ) : (
                      <p>No Data To Show</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col sm={6} md={6} lg={6} xl={6}>
                <Card>
                  <Card.Body>
                    <h5 className="card-title mb-3">Payment Details</h5>

                    {!checkEmpty(filteredPayments) ? (
                      <Table className="table table-invoice table-bordered">
                        <thead>
                          <tr>
                            <th className="wd-20p">Date</th>
                            <th className="wd-40p">Description</th>
                            <th className="wd-40p">Receipt Voucher</th>
                            <th className="tx-right">Amount</th>
                          </tr>
                        </thead>

                        <tbody>
                          {filteredPayments.map((item, index) => (
                            <tr key={`${item?.voucher_No ?? 'VCH'}-${index}`}>
                              <td>{toYMD(item?.payment_Date) || '-'}</td>
                              <td className="tx-12">Payment</td>
                              <td className="tx-12">{item?.voucher_No || '-'}</td>
                              <td className="tx-right">{number_format(item?.voucher_payment_Amount, 2)}</td>
                            </tr>
                          ))}

                          <tr className="total-row">
                            <th colSpan={2}>TOTAL</th>
                            <th></th>
                            <th className="tx-right">{number_format(periodTotals.paidTotal, 2)}</th>
                          </tr>
                        </tbody>
                      </Table>
                    ) : (
                      <p>No Data To Show</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col sm={12} md={12} lg={12} xl={12}>
                <span className="text-center">
                  {type === 'customer' ? (
                    <p>
                      Your account balance is {number_format(periodTotals.dueTotal, 2)}. Please make your payment to
                      cover the balance by the due date.
                    </p>
                  ) : (
                    <p>
                      Vendor payable balance of QAR {number_format(periodTotals.dueTotal, 2)} is due and is paid by due
                      date.
                    </p>
                  )}
                  <h5>Thank you for your business</h5>
                  <p>Should you have any enquiries concerning this statement, please contact on +974-44680850</p>
                </span>
              </Col>
            </Row>
          </Card.Body>

          <Card.Footer className="text-end">
            <Button variant="" type="button" className="btn ripple btn-info mb-1 me-2 print-hidden" onClick={print}>
              <i className="fe fe-printer me-1"></i> Print
            </Button>
          </Card.Footer>
        </Card>
      </Col>
    </Row>
  );
};

export default Statement;
